package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.aichat.createAiChatSession
import com.royceecoleman.bill.aichat.fetchAiChatSessionTurns
import com.royceecoleman.bill.aichat.fetchAiChatSessions
import com.royceecoleman.bill.aichat.fetchAiModels
import com.royceecoleman.bill.aichat.sendAiChatMessage
import com.royceecoleman.bill.aichat.updateAiChatSessionModel
import com.royceecoleman.bill.auth.AuthenticatedSession
import com.royceecoleman.bill.auth.canAccessAdminDashboard
import com.royceecoleman.bill.shared.aichat.AiChatSessionDto
import com.royceecoleman.bill.shared.aichat.AiModelDto
import com.royceecoleman.bill.shared.aichat.ChatTurnDto
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

@Page("/ai-chat")
@Composable
fun aiChatPage() {
    GuardedAuthenticatedPage(
        checkingMessage = "Loading your AI chat workspace.",
        redirectingToLoginMessage = "You need to sign in before opening AI chat.",
    ) { authenticatedSession ->
        AiChatPageContent(authenticatedSession)
    }
}

@Composable
private fun AiChatPageContent(authenticatedSession: AuthenticatedSession) {
    val coroutineScope = rememberCoroutineScope()
    var availableModels by remember { mutableStateOf(listOf<AiModelDto>()) }
    var sessions by remember { mutableStateOf(listOf<AiChatSessionDto>()) }
    var selectedSessionId by remember { mutableStateOf<Long?>(null) }
    var messageInput by remember { mutableStateOf("") }
    var messages by remember { mutableStateOf(listOf<ChatTurnDto>()) }
    var selectedModelId by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var isSubmitting by remember { mutableStateOf(false) }
    var isCreatingSession by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    suspend fun loadSession(sessionId: Long) {
        messages = fetchAiChatSessionTurns(sessionId)
        selectedSessionId = sessionId
        selectedModelId = sessions.firstOrNull { it.id == sessionId }?.model
    }

    suspend fun refreshSessions(preferredSessionId: Long? = selectedSessionId) {
        val refreshedSessions = visibleAiChatSessionsForUser(authenticatedSession.id, fetchAiChatSessions())
        sessions = refreshedSessions

        val resolvedSessionId =
            when {
                preferredSessionId != null && refreshedSessions.any { it.id == preferredSessionId } -> preferredSessionId
                refreshedSessions.isNotEmpty() -> refreshedSessions.first().id
                else -> null
            }

        if (resolvedSessionId == null) {
            messages = emptyList()
            selectedSessionId = null
            selectedModelId = availableModels.firstOrNull { it.isDefault }?.id ?: availableModels.firstOrNull()?.id
            return
        }

        loadSession(resolvedSessionId)
    }

    suspend fun ensureInitialSession() {
        if (sessions.isNotEmpty()) {
            return
        }

        val createdSession = createAiChatSession()
        sessions = visibleAiChatSessionsForUser(authenticatedSession.id, listOf(createdSession))
        selectedSessionId = createdSession.id
        selectedModelId = createdSession.model
        messages = emptyList()
    }

    LaunchedEffect(Unit) {
        try {
            availableModels = fetchAiModels()
            selectedModelId = availableModels.firstOrNull { it.isDefault }?.id ?: availableModels.firstOrNull()?.id
            refreshSessions()
            ensureInitialSession()
            if (selectedSessionId != null) {
                loadSession(checkNotNull(selectedSessionId))
            }
        } catch (error: Throwable) {
            if (error is CancellationException) {
                throw error
            }
            errorMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to load AI chat."
        } finally {
            isLoading = false
        }
    }

    val selectedSession = sessions.firstOrNull { it.id == selectedSessionId }

    Main(
        attrs = {
            style {
                property("min-height", "100vh")
                property("padding", "1.5rem")
                property("background", "#0f172a")
                property("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("max-width", "88rem")
                    property("margin", "0 auto")
                    property("display", "grid")
                    property("gap", "1.25rem")
                }
            },
        ) {
            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "1rem")
                        property("flex-wrap", "wrap")
                    }
                },
            ) {
                Div {
                    H1(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("color", "#f8fafc")
                            }
                        },
                    ) {
                        Text("AI Chat")
                    }
                     P(
                         attrs = {
                             style {
                                 property("margin", "0.5rem 0 0")
                                 property("color", "#cbd5e1")
                             }
                         },
                     ) {
                        Text(aiChatHeaderSupportingText(authenticatedSession.username))
                     }
                }
                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("gap", "0.75rem")
                            property("flex-wrap", "wrap")
                        }
                    },
                ) {
                    if (authenticatedSession.role.canAccessAdminDashboard()) {
                        SecondaryActionButton(
                            label = "Dashboard",
                            onClick = { window.location.href = "/dashboard" },
                        )
                    }
                    SecondaryActionButton(
                        label = "Home",
                        onClick = { window.location.href = "/" },
                    )
                }
            }

            if (errorMessage != null) {
                FeedbackBanner(
                    message = checkNotNull(errorMessage),
                    isError = true,
                )
            }

            Div(
                attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "minmax(18rem, 24rem) minmax(0, 1fr)")
                        property("gap", "1rem")
                    }
                },
            ) {
                Div(
                    attrs = {
                        style {
                            property("padding", "1.25rem")
                            property("border-radius", "1rem")
                            property("background", "rgba(255, 255, 255, 0.97)")
                            property("box-shadow", "0 24px 60px rgba(15, 23, 42, 0.35)")
                            property("display", "grid")
                            property("gap", "1rem")
                            property("align-content", "start")
                        }
                    },
                ) {
                    Div(
                        attrs = {
                            style {
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "0.75rem")
                            }
                        },
                    ) {
                        Div {
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0")
                                        property("font-weight", "700")
                                        property("color", "#0f172a")
                                    }
                                },
                            ) {
                                Text("Sessions")
                            }
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0.35rem 0 0")
                                        property("color", "#64748b")
                                        property("font-size", "0.95rem")
                                    }
                                },
                            ) {
                                Text("Pick a prior conversation or start a new one.")
                            }
                        }
                        Button(
                            attrs = {
                                onClick {
                                    if (isCreatingSession) {
                                        return@onClick
                                    }
                                    coroutineScope.launch {
                                        isCreatingSession = true
                                        errorMessage = null
                                        try {
                                            val createdSession = createAiChatSession()
                                            sessions =
                                                visibleAiChatSessionsForUser(
                                                    authenticatedSession.id,
                                                    listOf(createdSession) + sessions,
                                                )
                                            selectedSessionId = createdSession.id
                                            selectedModelId = createdSession.model
                                            messages = emptyList()
                                        } catch (error: Throwable) {
                                            if (error is CancellationException) {
                                                throw error
                                            }
                                            errorMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to create a chat session."
                                        } finally {
                                            isCreatingSession = false
                                        }
                                    }
                                }
                                style {
                                    property("padding", "0.75rem 0.9rem")
                                    property("border", "0")
                                    property("border-radius", "0.75rem")
                                    property("background", "#2563eb")
                                    property("color", "#ffffff")
                                    property("font-weight", "700")
                                    property("cursor", "pointer")
                                }
                            },
                        ) {
                            Text(if (isCreatingSession) "Creating..." else "New chat")
                        }
                    }

                    if (sessions.isEmpty() && !isLoading) {
                        P(
                            attrs = {
                                style {
                                    property("margin", "0")
                                    property("color", "#64748b")
                                }
                            },
                        ) {
                            Text("No chat sessions yet.")
                        }
                    } else {
                        Div(
                            attrs = {
                                style {
                                    property("display", "grid")
                                    property("gap", "0.75rem")
                                }
                            },
                        ) {
                            sessions.forEach { session ->
                                Button(
                                    attrs = {
                                        onClick {
                                            coroutineScope.launch {
                                                errorMessage = null
                                                try {
                                                    loadSession(session.id)
                                                } catch (error: Throwable) {
                                                    if (error is CancellationException) {
                                                        throw error
                                                    }
                                                    errorMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to load the selected session."
                                                }
                                            }
                                        }
                                        style {
                                            property("padding", "0.9rem 1rem")
                                            property("border", if (session.id == selectedSessionId) "2px solid #2563eb" else "1px solid #cbd5e1")
                                            property("border-radius", "0.85rem")
                                            property("background", if (session.id == selectedSessionId) "#dbeafe" else "#f8fafc")
                                            property("color", "#0f172a")
                                            property("text-align", "left")
                                            property("cursor", "pointer")
                                        }
                                    },
                                ) {
                                    Text(session.name)
                                }
                            }
                        }
                    }
                }

                Div(
                    attrs = {
                        style {
                            property("padding", "1.25rem")
                            property("border-radius", "1rem")
                            property("background", "rgba(255, 255, 255, 0.97)")
                            property("box-shadow", "0 24px 60px rgba(15, 23, 42, 0.35)")
                            property("display", "grid")
                            property("gap", "1rem")
                        }
                    },
                ) {
                    Div(
                        attrs = {
                            style {
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "1rem")
                                property("flex-wrap", "wrap")
                            }
                        },
                    ) {
                        Div {
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0")
                                        property("font-size", "1.1rem")
                                        property("font-weight", "700")
                                    }
                                },
                            ) {
                                Text(selectedSession?.name ?: "Current chat")
                            }
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0.35rem 0 0")
                                        property("color", "#64748b")
                                    }
                                },
                            ) {
                                Text(AI_CHAT_CONVERSATION_SUPPORTING_TEXT)
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    property("display", "grid")
                                    property("gap", "0.35rem")
                                    property("min-width", "14rem")
                                }
                            },
                        ) {
                            Label(forId = "ai-model-select") {
                                Text("Model")
                            }
                            Select(
                                attrs = {
                                    id("ai-model-select")
                                    onChange { event ->
                                        val nextModelId = event.value ?: return@onChange
                                        val sessionId = selectedSessionId ?: return@onChange
                                        selectedModelId = nextModelId
                                        coroutineScope.launch {
                                            errorMessage = null
                                            try {
                                                val updatedSession = updateAiChatSessionModel(sessionId, nextModelId)
                                                sessions =
                                                    sessions.map { session ->
                                                        if (session.id == updatedSession.id) {
                                                            updatedSession
                                                        } else {
                                                            session
                                                        }
                                                    }
                                                selectedModelId = updatedSession.model
                                            } catch (error: Throwable) {
                                                if (error is CancellationException) {
                                                    throw error
                                                }
                                                errorMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to update the chat model."
                                                selectedModelId = selectedSession?.model
                                            }
                                        }
                                    }
                                    style {
                                        property("padding", "0.8rem 0.95rem")
                                        property("border", "1px solid #cbd5e1")
                                        property("border-radius", "0.75rem")
                                        property("background", "#ffffff")
                                    }
                                },
                            ) {
                                availableModels.forEach { model ->
                                    Option(
                                        value = model.id,
                                        attrs = {
                                            if (model.id == (selectedModelId ?: selectedSession?.model)) {
                                                attr("selected", "selected")
                                            }
                                        },
                                    ) {
                                        Text(model.name)
                                    }
                                }
                            }
                        }
                    }

                    Div(
                        attrs = {
                            style {
                                property("display", "grid")
                                property("gap", "0.75rem")
                                property("min-height", "26rem")
                                property("max-height", "34rem")
                                property("overflow-y", "auto")
                                property("padding", "0.25rem")
                            }
                        },
                    ) {
                        if (messages.isEmpty()) {
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0")
                                        property("color", "#64748b")
                                    }
                                },
                            ) {
                                Text(if (isLoading) "Loading chat history..." else "Start the conversation with a message below.")
                            }
                        } else {
                            messages.forEach { turn ->
                                Div(
                                    attrs = {
                                        style {
                                            property("padding", "0.9rem 1rem")
                                            property("border-radius", "0.9rem")
                                            property(
                                                "background",
                                                if (turn.sender == "assistant") "#e0f2fe" else "#f8fafc",
                                            )
                                            property(
                                                "border",
                                                if (turn.sender == "assistant") "1px solid #7dd3fc" else "1px solid #cbd5e1",
                                            )
                                        }
                                    },
                                ) {
                                    P(
                                        attrs = {
                                            style {
                                                property("margin", "0")
                                                property("font-weight", "700")
                                                property("color", "#0f172a")
                                            }
                                        },
                                    ) {
                                        Text(if (turn.sender == "assistant") "Assistant" else "You")
                                    }
                                    if (turn.sender == "assistant") {
                                        AiChatMarkdownContent(markdown = turn.content)
                                    } else {
                                        P(
                                            attrs = {
                                                style {
                                                    property("margin", "0.45rem 0 0")
                                                    property("line-height", "1.6")
                                                    property("white-space", "pre-wrap")
                                                    property("color", "#334155")
                                                }
                                            },
                                        ) {
                                            Text(turn.content)
                                        }
                                    }
                                }
                            }
                        }
                    }

                    Div(
                        attrs = {
                            style {
                                property("display", "grid")
                                property("gap", "0.75rem")
                            }
                        },
                    ) {
                        Label(forId = "ai-chat-input") {
                            Text("Message")
                        }
                        TextArea(
                            value = messageInput,
                            attrs = {
                                id("ai-chat-input")
                                onInput { messageInput = it.value }
                                attr("rows", "5")
                                style {
                                    property("width", "100%")
                                    property("padding", "0.9rem 1rem")
                                    property("border", "1px solid #cbd5e1")
                                    property("border-radius", "0.85rem")
                                    property("font-size", "1rem")
                                    property("box-sizing", "border-box")
                                    property("resize", "vertical")
                                }
                            },
                        )
                        Div(
                            attrs = {
                                style {
                                    property("display", "flex")
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                    property("gap", "0.75rem")
                                    property("flex-wrap", "wrap")
                                }
                            },
                        ) {
                            P(
                                attrs = {
                                    style {
                                        property("margin", "0")
                                        property("color", "#64748b")
                                    }
                                },
                            ) {
                                Text("The current chat session stays selected while you send messages.")
                            }
                            Button(
                                attrs = {
                                    onClick {
                                        val trimmedMessage = messageInput.trim()
                                        val sessionId = selectedSessionId ?: return@onClick
                                        if (trimmedMessage.isEmpty() || isSubmitting) {
                                            return@onClick
                                        }
                                        coroutineScope.launch {
                                            isSubmitting = true
                                            errorMessage = null
                                            try {
                                                sendAiChatMessage(sessionId, trimmedMessage)
                                                messageInput = ""
                                                refreshSessions(sessionId)
                                            } catch (error: Throwable) {
                                                if (error is CancellationException) {
                                                    throw error
                                                }
                                                errorMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to send the AI chat message."
                                            } finally {
                                                isSubmitting = false
                                            }
                                        }
                                    }
                                    style {
                                        property("padding", "0.9rem 1rem")
                                        property("border", "0")
                                        property("border-radius", "0.8rem")
                                        property("background", "#2563eb")
                                        property("color", "#ffffff")
                                        property("font-size", "1rem")
                                        property("font-weight", "700")
                                        property("cursor", "pointer")
                                    }
                                },
                            ) {
                                Text(if (isSubmitting) "Sending..." else "Send message")
                            }
                        }
                    }
                }
            }
        }
    }
}

internal fun visibleAiChatSessionsForUser(
    authenticatedUserId: Int,
    sessions: List<AiChatSessionDto>,
): List<AiChatSessionDto> = sessions.filter { it.ownerId == authenticatedUserId }

@Composable
private fun SecondaryActionButton(
    label: String,
    onClick: () -> Unit,
) {
    Button(
        attrs = {
            onClick { onClick() }
            style {
                property("padding", "0.85rem 1rem")
                property("border", "1px solid rgba(255, 255, 255, 0.35)")
                property("border-radius", "0.8rem")
                property("background", "transparent")
                property("color", "#f8fafc")
                property("font-weight", "700")
                property("cursor", "pointer")
            }
        },
    ) {
        Text(label)
    }
}

@Composable
private fun FeedbackBanner(
    message: String,
    isError: Boolean,
) {
    Div(
        attrs = {
            style {
                property("padding", "0.9rem 1rem")
                property("border-radius", "0.85rem")
                property("background", if (isError) "#fee2e2" else "#dcfce7")
                property("color", if (isError) "#b91c1c" else "#166534")
            }
        },
    ) {
        Text(message)
    }
}
